import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Truck, CheckCircle, Clock, ChevronRight, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Orders = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Translations
  const texts = {
    tr: {
      pageTitle: 'Siparişlerim',
      pageSubtitle: 'Tüm siparişlerinizi buradan takip edebilirsiniz',
      noOrders: 'Henüz Siparişiniz Yok',
      noOrdersDesc: 'İlk siparişinizi verin ve lezzetli yemeklerimizin tadını çıkarın.',
      orderNow: 'Sipariş Ver',
      order: 'Sipariş',
      items: 'ürün',
      preparing: 'Hazırlanıyor',
      onTheWay: 'Yolda',
      delivered: 'Teslim Edildi'
    },
    en: {
      pageTitle: 'My Orders',
      pageSubtitle: 'Track all your orders here',
      noOrders: 'No Orders Yet',
      noOrdersDesc: 'Place your first order and enjoy our delicious food.',
      orderNow: 'Order Now',
      order: 'Order',
      items: 'items',
      preparing: 'Preparing',
      onTheWay: 'On the Way',
      delivered: 'Delivered'
    },
    me: {
      pageTitle: 'Moje Narudžbe',
      pageSubtitle: 'Pratite sve svoje narudžbe ovdje',
      noOrders: 'Još Nema Narudžbi',
      noOrdersDesc: 'Napravite prvu narudžbu i uživajte u našoj hrani.',
      orderNow: 'Naruči Sada',
      order: 'Narudžba',
      items: 'artikala',
      preparing: 'Priprema',
      onTheWay: 'Na Putu',
      delivered: 'Dostavljeno'
    }
  };

  const txt = texts[language] || texts.en;
  const dateLocale = language === 'tr' ? tr : enUS;

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Orders fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'hazirlaniyor':
        return { icon: Package, label: txt.preparing, color: 'text-amber-600', bgColor: 'bg-amber-50' };
      case 'yolda':
        return { icon: Truck, label: txt.onTheWay, color: 'text-blue-600', bgColor: 'bg-blue-50' };
      case 'teslim_edildi':
        return { icon: CheckCircle, label: txt.delivered, color: 'text-green-600', bgColor: 'bg-green-50' };
      default:
        return { icon: Clock, label: status, color: 'text-gray-600', bgColor: 'bg-gray-50' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-terracotta border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pb-8">
      {/* Header */}
      <div className="bg-sand py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-2">
              {txt.pageTitle}
            </h1>
            <p className="text-charcoal/60">
              {txt.pageSubtitle}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <ShoppingBag className="w-20 h-20 text-charcoal/20 mx-auto mb-6" />
            <h2 className="font-playfair text-2xl font-semibold text-charcoal mb-4">
              {txt.noOrders}
            </h2>
            <p className="text-charcoal/60 mb-8">
              {txt.noOrdersDesc}
            </p>
            <Link to="/alakart">
              <Button className="bg-terracotta text-white hover:bg-terracotta/90 rounded-full px-8" data-testid="start-ordering-btn">
                {txt.orderNow}
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => {
              const statusInfo = getStatusInfo(order.status);
              const StatusIcon = statusInfo.icon;
              
              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/orders/${order.id}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-medium text-charcoal">
                              {txt.order} #{order.id.slice(-8).toUpperCase()}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusInfo.label}
                            </span>
                          </div>
                          <p className="text-charcoal/60 text-sm mb-2">
                            {format(new Date(order.created_at), 'd MMMM yyyy, HH:mm', { locale: dateLocale })}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-charcoal/70">
                            <span>{order.items.length} {txt.items}</span>
                            <span>•</span>
                            <span className="font-semibold text-terracotta">€{order.total_price.toFixed(2)}</span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-charcoal/40" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
